import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { Button } from 'reactstrap';
import { SquareSwatch } from './SquareSwatch';
import { TilesetKey } from '../../types/tileset';

type OnClickHexLabel = Nullable<
  (e: MouseEvent, hexCode: string, label: string) => void
>;

interface DiscreteKeyRowProps {
  colorToHighlight: Nullable<string>;
  hexCode: string;
  label: string;
  onClick?: OnClickHexLabel;
  shouldScrollToHighlight?: boolean;
}

interface DiscreteKeyProps {
  keys: TilesetKey[];
  colorToHighlight: Nullable<string>;
  maxHeight?: string;
  onClick?: OnClickHexLabel;
}

const SquareSwatchWithOptionalBorder = styled(SquareSwatch)`
  outline-style: solid;
  outline-width: 2px;
  outline-color: ${({ isHovered }: { isHovered?: boolean }) =>
    isHovered ? 'black' : 'transparent'};
  margin-top: 3px;
`;

const InvisibleButton = styled(Button)`
  background-color: transparent;
  color: var(--text-color);
  font-size: 1em;
  line-height: 1.4;
  margin-top: -1px;
  padding: 0;
  text-align: left;
  &:hover {
    text-decoration: ${({ onClick }) => (onClick ? 'underline' : 'none')};
  }
`;

const DiscreteKeyRow = ({
  colorToHighlight,
  hexCode,
  label,
  onClick = null,
  shouldScrollToHighlight = false,
}: DiscreteKeyRowProps) => {
  const ref = useRef<null | HTMLDivElement>(null);
  const isHovered = hexCode.toLowerCase() === colorToHighlight?.toLowerCase();

  // If `shouldScrollToHighlight`, scroll to the highlighted color if it is hovered:
  useEffect(() => {
    if (isHovered && shouldScrollToHighlight && ref.current) {
      ref.current.scrollIntoView({ block: 'center' });
    }
  }, [isHovered, shouldScrollToHighlight]);

  return (
    <div ref={ref} className="d-flex align-items-start m-1">
      <SquareSwatchWithOptionalBorder
        color={`${hexCode}`}
        borderColor={`${hexCode}`}
        className="mr-2 flex-shrink-0"
        isHovered={isHovered}
      />
      {onClick ? (
        <InvisibleButton
          color="link"
          onClick={(e: MouseEvent) => onClick(e, hexCode, label)}
        >
          {label || ''}
        </InvisibleButton>
      ) : (
        <span>{label || ''}</span>
      )}
    </div>
  );
};

const DiscreteKey = ({
  keys,
  colorToHighlight,
  maxHeight = '400px',
  onClick = null,
}: DiscreteKeyProps) => {
  if (keys.length < 16) {
    return keys.map(({ hexCode, label }) => (
      <DiscreteKeyRow
        key={hexCode}
        colorToHighlight={colorToHighlight}
        hexCode={hexCode}
        label={label}
        onClick={onClick}
      />
    ));
  }

  // If there are more than 16 keys, display them within a scrollable container
  // and scroll to the highlighted color when it changes:
  return (
    <div
      className="d-flex flex-column"
      style={{ maxHeight, overflowY: 'auto' }}
    >
      {keys.map(({ hexCode, label }) => (
        <DiscreteKeyRow
          key={hexCode}
          colorToHighlight={colorToHighlight}
          hexCode={hexCode}
          label={label}
          onClick={onClick}
          shouldScrollToHighlight
        />
      ))}
    </div>
  );
};

export default DiscreteKey;
